/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import { useContext, useMemo } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { sortObjectArray } from 'utils/arrayHelpers';
import { currentUtcMillis } from 'utils/dateManagement';

export const useOrdersDisplay = ({ processingOrders, refresher, companySettings }) => {
  const { state: auth } = useContext(AuthContext);

  // new, in_progress, ready, closed
  const [visibleTab, setVisibleTab] = useLocalStorage(`orders-visible-tab-${auth.company?.company}`, 'new');

  const {
    filteredOrders,
    totalNew,
    totalNewTabCombined,
    totalInProgress,
    totalDelivering,
    totalReady,
  } = useMemo(() => {
    if (!processingOrders || processingOrders.length === 0) {
      return {
        filteredOrders: [],
        totalNew: 0,
        totalNewTabCombined: 0,
        totalInProgress: 0,
        totalDelivering: 0,
        totalReady: 0,
      };
    }

    let filteredOrdersTemp = [];

    if (visibleTab === 'new') {
      filteredOrdersTemp = processingOrders.filter((x) => !x.progress_status || x.progress_status === 'New'
        || (x.progress_status === 'In_Progress' && (x.keep_in_new || (companySettings && !companySettings.separate_in_progress)))
        || (x.progress_status === 'Delivering' && (x.keep_in_new || (companySettings && !companySettings.separate_delivering))));
    } else if (visibleTab === 'in_progress') {
      filteredOrdersTemp = processingOrders.filter((x) => {
        x.keep_in_new = false;
        return x.progress_status === 'In_Progress';
      });
    } else if (visibleTab === 'delivering') {
      filteredOrdersTemp = processingOrders.filter((x) => {
        x.keep_in_new = false;
        return x.progress_status === 'Delivering';
      });
    } else if (visibleTab === 'ready') {
      filteredOrdersTemp = processingOrders.filter((x) => {
        x.keep_in_new = false;
        return x.progress_status === 'Ready';
      });
    }

    const currentMillis = currentUtcMillis();

    filteredOrdersTemp.forEach((order) => {
      // if new or in_progress - use prepare_date
      // if other statuses, use delivery_date
      let deliveryDateToUse;

      // New or in progress
      if (!order.progress_status || order.progress_status === 'New' || order.progress_status === 'In_Progress') {
        if (order.deliveryOrder && order.deliveryOrder.inform_type === 'Wolt' && order.deliveryOrder.pickup_date) {
          deliveryDateToUse = order.deliveryOrder.pickup_date;
        } else {
          deliveryDateToUse = order.prepare_date ? order.prepare_date : order.delivery_date;
        }
      // If other status
      } else if (order.deliveryOrder && order.deliveryOrder.inform_type === 'Wolt' && order.deliveryOrder.delivery_date) {
        deliveryDateToUse = order.deliveryOrder.delivery_date;
      } else {
        deliveryDateToUse = order.delivery_date;
      }

      const minutesDif = (currentMillis - (deliveryDateToUse || (order.date_utc + 30 * 60000))) / 60000;

      order.delivery_dif = parseInt(minutesDif, 10);

      // now, future, late
      order.prepare_status = minutesDif > 0 ? 'late' : minutesDif < -30 ? 'future' : 'now';
    });

    // If ordered in restaurant - Now
    // If delivery/takeaway now - Now
    // If delivery/takeaway later - Later

    sortObjectArray(filteredOrdersTemp, 'delivery_dif', -1, true);

    return {
      filteredOrders: filteredOrdersTemp,
      totalNew: processingOrders.filter((x) => !x.progress_status || x.progress_status === 'New').length,
      totalNewTabCombined: processingOrders.filter((x) => !x.progress_status || x.progress_status === 'New'
        || (x.progress_status === 'In_Progress' && (x.keep_in_new || (companySettings && !companySettings.separate_in_progress)))
        || (x.progress_status === 'Delivering' && (x.keep_in_new || (companySettings && !companySettings.separate_delivering)))).length,
      totalInProgress: processingOrders.filter((x) => x.progress_status === 'In_Progress').length,
      totalDelivering: processingOrders.filter((x) => x.progress_status === 'Delivering').length,
      totalReady: processingOrders.filter((x) => x.progress_status === 'Ready').length,
    };
  }, [visibleTab, processingOrders, refresher, companySettings]);

  return {
    visibleTab,
    setVisibleTab,
    orders: filteredOrders,
    totalNew,
    totalNewTabCombined,
    totalInProgress,
    totalDelivering,
    totalReady,
  };
};
